import { APIKit } from "config/axios";
import store from "config/redux";
import { end, start } from "config/redux/slice";

import { RouteConstants, ServerConstants, ServiceConstants } from "constant";
import { ServiceModel } from "models";
import {
  AdminDashboardScope,
  BankDashboardStatusResponse,
  DashboardAgentFilterParams,
  DashboardAgentOptionResponse,
  DashboardCalendarParams,
  DashboardCalendarResponse,
  DashboardCallOutcomeRequest,
  DashboardCallWidgetClientResponse,
  DashboardCallWidgetSearchParams,
  DashboardCallWidgetSearchResultResponse,
  DashboardCallWidgetToggleRequest,
  DashboardCallWidgetToggleResponse,
  DashboardScheduleCallRequest,
  DashboardDailyStateItemResponse,
  DashboardDailyStateItemType,
  DashboardDailyStateResponse,
  DashboardAgentResponse,
  DashboardConversionHealthResponse,
  DashboardDelayedProcessingAlarmCountResponse,
  DashboardDelayedProcessingAlarmEntryResponse,
  DashboardKpiParams,
  DashboardAgentKpisResponse,
  DashboardKpisResponse,
  DashboardMonthlyProgressResponse,
  DashboardScope,
  DashboardTeamLeadDailyKpisResponse,
  DashboardYearlyProgressResponse,
} from "types";
import { AppUtils } from "utils";

import { handleServiceError } from "./common";
import * as PublicService from "./public";

const { ROUTE_LOGIN } = RouteConstants;
const {
  API_V1_AGENT_DASHBOARD_CALENDAR,
  API_V1_AGENT_DASHBOARD_KPIS,
  API_V1_AGENT_DASHBOARD_NEXT,
  API_V1_AGENT_DASHBOARD_SEARCH,
  API_V1_AGENT_DASHBOARD_CLIENTS,
  API_V1_AGENT_DASHBOARD_SCHEDULED_CALLS,
  API_V1_USERS,
  API_V1_TODO,
  API_V1_ADMIN_DASHBOARD_BANK_STATUS,
  API_V1_ADMIN_DASHBOARD_CONVERSION_HEALTH,
  API_V1_ADMIN_DASHBOARD_DELAYED_PROCESSING_ALARM_COUNT,
  API_V1_ADMIN_DASHBOARD_DELAYED_PROCESSING_ALARM_INFO,
  API_V1_ADMIN_DASHBOARD_AGENTS,
  API_V1_ADMIN_DASHBOARD_KPIS,
  API_V1_ADMIN_DASHBOARD_MONTHLY_PROGRESS,
  API_V1_ADMIN_DASHBOARD_YEARLY_PROGRESS,
  API_V1_ADMIN_REPORT,
  API_V1_ADMINISTRATOR_DASHBOARD_AGENTS,
  API_V1_ADMINISTRATOR_DASHBOARD_BANK_STATUS,
  API_V1_ADMINISTRATOR_DASHBOARD_CONVERSION_HEALTH,
  API_V1_ADMINISTRATOR_DASHBOARD_DELAYED_PROCESSING_ALARM_COUNT,
  API_V1_ADMINISTRATOR_DASHBOARD_DELAYED_PROCESSING_ALARM_INFO,
  API_V1_ADMINISTRATOR_DASHBOARD_KPIS,
  API_V1_ADMINISTRATOR_DASHBOARD_MONTHLY_PROGRESS,
  API_V1_ADMINISTRATOR_DASHBOARD_YEARLY_PROGRESS,
  API_V1_TEAM_LEAD_DASHBOARD_AGENTS,
  API_V1_TEAM_LEAD_DASHBOARD_BANK_STATUS,
  API_V1_TEAM_LEAD_DASHBOARD_CALENDAR,
  API_V1_TEAM_LEAD_DASHBOARD_CONVERSION_HEALTH,
  API_V1_TEAM_LEAD_DASHBOARD_DAILY_KPIS,
  API_V1_TEAM_LEAD_DASHBOARD_DAILY_STATE,
  API_V1_TEAM_LEAD_DASHBOARD_DAILY_STATE_ITEMS,
  API_V1_TEAM_LEAD_DASHBOARD_DELAYED_PROCESSING_ALARM_COUNT,
  API_V1_TEAM_LEAD_DASHBOARD_DELAYED_PROCESSING_ALARM_INFO,
  API_V1_TEAM_LEAD_DASHBOARD_KPIS,
  API_V1_TEAM_LEAD_DASHBOARD_MONTHLY_PROGRESS,
} = ServerConstants;
const {
  REQUEST_AGENT_DASHBOARD_CALENDAR_ID,
  REQUEST_AGENT_DASHBOARD_KPIS_ID,
  REQUEST_AGENT_DASHBOARD_NEXT_ID,
  REQUEST_AGENT_DASHBOARD_SEARCH_ID,
  REQUEST_AGENT_DASHBOARD_CLIENT_TOGGLE_ID,
  REQUEST_AGENT_DASHBOARD_CALL_OUTCOME_ID,
  REQUEST_AGENT_DASHBOARD_SCHEDULED_CALLS_ID,
  REQUEST_AGENT_DASHBOARD_AGENT_OPTIONS_ID,
  REQUEST_AGENT_DASHBOARD_CANCEL_TODO_ID,
  REQUEST_ADMIN_DASHBOARD_AGENTS_ID,
  REQUEST_ADMIN_DASHBOARD_BANK_STATUS_ID,
  REQUEST_ADMIN_DASHBOARD_CONVERSION_HEALTH_ID,
  REQUEST_ADMIN_DASHBOARD_DELAYED_PROCESSING_ALARM_COUNT_ID,
  REQUEST_ADMIN_DASHBOARD_DELAYED_PROCESSING_ALARM_INFO_ID,
  REQUEST_ADMIN_DASHBOARD_KPIS_ID,
  REQUEST_ADMIN_DASHBOARD_MONTHLY_PROGRESS_ID,
  REQUEST_ADMIN_DASHBOARD_YEARLY_PROGRESS_ID,
  REQUEST_TEAM_LEAD_DASHBOARD_CALENDAR_ID,
  REQUEST_TEAM_LEAD_DASHBOARD_DAILY_KPIS_ID,
  REQUEST_TEAM_LEAD_DASHBOARD_DAILY_STATE_ID,
  REQUEST_TEAM_LEAD_DASHBOARD_DAILY_STATE_ITEMS_ID,
} = ServiceConstants;

const DASHBOARD_ENDPOINTS: Record<
  DashboardScope,
  {
    agents: string;
    bankStatus: string;
    conversionHealth: string;
    delayedProcessingAlarmCount: string;
    delayedProcessingAlarmInfo: string;
    kpis: string;
    monthlyProgress: string;
  }
> = {
  admin: {
    agents: API_V1_ADMIN_DASHBOARD_AGENTS,
    bankStatus: API_V1_ADMIN_DASHBOARD_BANK_STATUS,
    conversionHealth: API_V1_ADMIN_DASHBOARD_CONVERSION_HEALTH,
    delayedProcessingAlarmCount: API_V1_ADMIN_DASHBOARD_DELAYED_PROCESSING_ALARM_COUNT,
    delayedProcessingAlarmInfo: API_V1_ADMIN_DASHBOARD_DELAYED_PROCESSING_ALARM_INFO,
    kpis: API_V1_ADMIN_DASHBOARD_KPIS,
    monthlyProgress: API_V1_ADMIN_DASHBOARD_MONTHLY_PROGRESS,
  },
  administrator: {
    agents: API_V1_ADMINISTRATOR_DASHBOARD_AGENTS,
    bankStatus: API_V1_ADMINISTRATOR_DASHBOARD_BANK_STATUS,
    conversionHealth: API_V1_ADMINISTRATOR_DASHBOARD_CONVERSION_HEALTH,
    delayedProcessingAlarmCount: API_V1_ADMINISTRATOR_DASHBOARD_DELAYED_PROCESSING_ALARM_COUNT,
    delayedProcessingAlarmInfo: API_V1_ADMINISTRATOR_DASHBOARD_DELAYED_PROCESSING_ALARM_INFO,
    kpis: API_V1_ADMINISTRATOR_DASHBOARD_KPIS,
    monthlyProgress: API_V1_ADMINISTRATOR_DASHBOARD_MONTHLY_PROGRESS,
  },
  teamLead: {
    agents: API_V1_TEAM_LEAD_DASHBOARD_AGENTS,
    bankStatus: API_V1_TEAM_LEAD_DASHBOARD_BANK_STATUS,
    conversionHealth: API_V1_TEAM_LEAD_DASHBOARD_CONVERSION_HEALTH,
    delayedProcessingAlarmCount: API_V1_TEAM_LEAD_DASHBOARD_DELAYED_PROCESSING_ALARM_COUNT,
    delayedProcessingAlarmInfo: API_V1_TEAM_LEAD_DASHBOARD_DELAYED_PROCESSING_ALARM_INFO,
    kpis: API_V1_TEAM_LEAD_DASHBOARD_KPIS,
    monthlyProgress: API_V1_TEAM_LEAD_DASHBOARD_MONTHLY_PROGRESS,
  },
};

const DASHBOARD_YEARLY_PROGRESS_ENDPOINTS: Record<AdminDashboardScope, string> = {
  admin: API_V1_ADMIN_DASHBOARD_YEARLY_PROGRESS,
  administrator: API_V1_ADMINISTRATOR_DASHBOARD_YEARLY_PROGRESS,
};

const DASHBOARD_REQUEST_ID_SUFFIXES: Record<DashboardScope, string | null> = {
  admin: null,
  administrator: "ADMINISTRATOR",
  teamLead: "TEAM_LEAD",
};

const getScopedRequestId = (baseRequestId: string, scope: DashboardScope) => {
  const suffix = DASHBOARD_REQUEST_ID_SUFFIXES[scope];

  return suffix ? `${baseRequestId}_${suffix}` : baseRequestId;
};

type DashboardRequestParamValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | Array<string | number | boolean>;

const createDashboardRequestConfig = <T extends object>(
  params?: T,
) => {
  if (!params) {
    return undefined;
  }

  return {
    params,
    paramsSerializer: {
      serialize: (rawParams: Record<string, any>) => {
        const searchParams = new URLSearchParams();

        Object.entries(rawParams as Record<string, DashboardRequestParamValue>).forEach(([key, value]) => {
          if (value === undefined || value === null || value === "") {
            return;
          }

          if (Array.isArray(value)) {
            value.forEach((item) => {
              searchParams.append(key, String(item));
            });
            return;
          }

          searchParams.append(key, String(value));
        });

        return searchParams.toString();
      },
    },
  };
};

async function retryWithRenewal<T>(request: () => Promise<T>, retryOnUnauthorized = true): Promise<T> {
  try {
    return await request();
  } catch (err: any) {
    const errStatus = JSON.parse(JSON.stringify(err))?.status;

    if (errStatus === 401 && retryOnUnauthorized) {
      const renewed = await PublicService.renew();

      if (renewed) {
        return retryWithRenewal(request, false);
      }

      AppUtils.logout();
      if (window.location.pathname !== ROUTE_LOGIN) {
        window.location.href = ROUTE_LOGIN;
      }
    }

    throw err;
  }
}

export async function getAgents(scope: DashboardScope = "admin"): Promise<DashboardAgentResponse[]> {
  const requestId = getScopedRequestId(REQUEST_ADMIN_DASHBOARD_AGENTS_ID, scope);

  try {
    store.dispatch(start(requestId));
    const data = await retryWithRenewal(async () => {
      const response = await APIKit.get(DASHBOARD_ENDPOINTS[scope].agents);
      return response.data as DashboardAgentResponse[];
    });
    store.dispatch(end(new ServiceModel(requestId, data)));
    return data;
  } catch (err: any) {
    handleServiceError(requestId, err);
    throw err;
  }
}

export async function getBankStatus(
  scope: DashboardScope = "admin",
  params?: DashboardAgentFilterParams,
): Promise<BankDashboardStatusResponse[]> {
  const requestId = getScopedRequestId(REQUEST_ADMIN_DASHBOARD_BANK_STATUS_ID, scope);

  try {
    store.dispatch(start(requestId));
    const data = await retryWithRenewal(async () => {
      const response = await APIKit.get(
        DASHBOARD_ENDPOINTS[scope].bankStatus,
        createDashboardRequestConfig(params),
      );
      return response.data as BankDashboardStatusResponse[];
    });
    store.dispatch(end(new ServiceModel(requestId, data)));
    return data;
  } catch (err: any) {
    handleServiceError(requestId, err);
    throw err;
  }
}

export async function getKpis(
  params: DashboardKpiParams,
  scope: DashboardScope = "admin",
): Promise<DashboardKpisResponse> {
  const requestId = getScopedRequestId(REQUEST_ADMIN_DASHBOARD_KPIS_ID, scope);

  try {
    store.dispatch(start(requestId));
    const data = await retryWithRenewal(async () => {
      const response = await APIKit.get(
        DASHBOARD_ENDPOINTS[scope].kpis,
        createDashboardRequestConfig(params),
      );
      return response.data as DashboardKpisResponse;
    });
    store.dispatch(end(new ServiceModel(requestId, data)));
    return data;
  } catch (err: any) {
    handleServiceError(requestId, err);
    throw err;
  }
}

export async function downloadAdminReport(params: DashboardKpiParams): Promise<void> {
  const response = await retryWithRenewal(async () =>
    APIKit.get(API_V1_ADMIN_REPORT, {
      ...createDashboardRequestConfig(params),
      responseType: "blob",
      headers: { Accept: "application/pdf" },
    }),
  );

  const disposition = response.headers["content-disposition"] as string | undefined;
  const filenameMatch = disposition?.match(/filename\*?=(?:UTF-8''|")?([^;"]+)/i);
  const filename = filenameMatch?.[1] ? decodeURIComponent(filenameMatch[1].trim()) : "izvestaj-o-napretku.pdf";
  const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export async function getAgentKpis(): Promise<DashboardAgentKpisResponse> {
  const requestId = REQUEST_AGENT_DASHBOARD_KPIS_ID;

  try {
    store.dispatch(start(requestId));
    const data = await retryWithRenewal(async () => {
      const response = await APIKit.get(API_V1_AGENT_DASHBOARD_KPIS);
      return response.data as DashboardAgentKpisResponse;
    });
    store.dispatch(end(new ServiceModel(requestId, data)));
    return data;
  } catch (err: any) {
    handleServiceError(requestId, err);
    throw err;
  }
}

export async function getConversionHealth(
  params: DashboardKpiParams,
  scope: DashboardScope = "admin",
): Promise<DashboardConversionHealthResponse> {
  const requestId = getScopedRequestId(REQUEST_ADMIN_DASHBOARD_CONVERSION_HEALTH_ID, scope);

  try {
    store.dispatch(start(requestId));
    const data = await retryWithRenewal(async () => {
      const response = await APIKit.get(
        DASHBOARD_ENDPOINTS[scope].conversionHealth,
        createDashboardRequestConfig(params),
      );
      return response.data as DashboardConversionHealthResponse;
    });
    store.dispatch(end(new ServiceModel(requestId, data)));
    return data;
  } catch (err: any) {
    handleServiceError(requestId, err);
    throw err;
  }
}

export async function getDelayedProcessingAlarmCount(
  scope: DashboardScope = "admin",
  params?: DashboardAgentFilterParams,
): Promise<DashboardDelayedProcessingAlarmCountResponse> {
  const requestId = getScopedRequestId(REQUEST_ADMIN_DASHBOARD_DELAYED_PROCESSING_ALARM_COUNT_ID, scope);

  try {
    store.dispatch(start(requestId));
    const data = await retryWithRenewal(async () => {
      const response = await APIKit.get(
        DASHBOARD_ENDPOINTS[scope].delayedProcessingAlarmCount,
        createDashboardRequestConfig(params),
      );
      return response.data as DashboardDelayedProcessingAlarmCountResponse;
    });
    store.dispatch(end(new ServiceModel(requestId, data)));
    return data;
  } catch (err: any) {
    handleServiceError(requestId, err);
    throw err;
  }
}

export async function getDelayedProcessingAlarmInfo(
  scope: DashboardScope = "admin",
  params?: DashboardAgentFilterParams,
): Promise<DashboardDelayedProcessingAlarmEntryResponse[]> {
  const requestId = getScopedRequestId(REQUEST_ADMIN_DASHBOARD_DELAYED_PROCESSING_ALARM_INFO_ID, scope);

  try {
    store.dispatch(start(requestId));
    const data = await retryWithRenewal(async () => {
      const response = await APIKit.get(
        DASHBOARD_ENDPOINTS[scope].delayedProcessingAlarmInfo,
        createDashboardRequestConfig(params),
      );
      return response.data as DashboardDelayedProcessingAlarmEntryResponse[];
    });
    store.dispatch(end(new ServiceModel(requestId, data)));
    return data;
  } catch (err: any) {
    handleServiceError(requestId, err);
    throw err;
  }
}

export async function getMonthlyProgress(
  scope: DashboardScope = "admin",
  params?: DashboardAgentFilterParams,
): Promise<DashboardMonthlyProgressResponse> {
  const requestId = getScopedRequestId(REQUEST_ADMIN_DASHBOARD_MONTHLY_PROGRESS_ID, scope);

  try {
    store.dispatch(start(requestId));
    const data = await retryWithRenewal(async () => {
      const response = await APIKit.get(
        DASHBOARD_ENDPOINTS[scope].monthlyProgress,
        createDashboardRequestConfig(params),
      );
      return response.data as DashboardMonthlyProgressResponse;
    });
    store.dispatch(end(new ServiceModel(requestId, data)));
    return data;
  } catch (err: any) {
    handleServiceError(requestId, err);
    throw err;
  }
}

export async function getYearlyProgress(
  scope: AdminDashboardScope = "admin",
  params?: DashboardAgentFilterParams,
): Promise<DashboardYearlyProgressResponse> {
  const requestId = getScopedRequestId(REQUEST_ADMIN_DASHBOARD_YEARLY_PROGRESS_ID, scope);

  try {
    store.dispatch(start(requestId));
    const data = await retryWithRenewal(async () => {
      const response = await APIKit.get(
        DASHBOARD_YEARLY_PROGRESS_ENDPOINTS[scope],
        createDashboardRequestConfig(params),
      );
      return response.data as DashboardYearlyProgressResponse;
    });
    store.dispatch(end(new ServiceModel(requestId, data)));
    return data;
  } catch (err: any) {
    handleServiceError(requestId, err);
    throw err;
  }
}

export async function getDailyState(
  params?: DashboardAgentFilterParams,
): Promise<DashboardDailyStateResponse> {
  const requestId = REQUEST_TEAM_LEAD_DASHBOARD_DAILY_STATE_ID;

  try {
    store.dispatch(start(requestId));
    const data = await retryWithRenewal(async () => {
      const response = await APIKit.get(
        API_V1_TEAM_LEAD_DASHBOARD_DAILY_STATE,
        createDashboardRequestConfig(params),
      );
      return response.data as DashboardDailyStateResponse;
    });
    store.dispatch(end(new ServiceModel(requestId, data)));
    return data;
  } catch (err: any) {
    handleServiceError(requestId, err);
    throw err;
  }
}

export async function getDailyStateItems(
  type: DashboardDailyStateItemType,
  params?: DashboardAgentFilterParams,
): Promise<DashboardDailyStateItemResponse[]> {
  const requestId = `${REQUEST_TEAM_LEAD_DASHBOARD_DAILY_STATE_ITEMS_ID}_${type}`;

  try {
    store.dispatch(start(requestId));
    const data = await retryWithRenewal(async () => {
      const response = await APIKit.get(
        API_V1_TEAM_LEAD_DASHBOARD_DAILY_STATE_ITEMS,
        createDashboardRequestConfig({
          type,
          ...(params ?? {}),
        }),
      );
      return response.data as DashboardDailyStateItemResponse[];
    });
    store.dispatch(end(new ServiceModel(requestId, data)));
    return data;
  } catch (err: any) {
    handleServiceError(requestId, err);
    throw err;
  }
}

export async function getAgentCalendar(
  params: DashboardCalendarParams,
): Promise<DashboardCalendarResponse> {
  const requestId = REQUEST_AGENT_DASHBOARD_CALENDAR_ID;

  try {
    store.dispatch(start(requestId));
    const data = await retryWithRenewal(async () => {
      const response = await APIKit.get(
        API_V1_AGENT_DASHBOARD_CALENDAR,
        createDashboardRequestConfig(params),
      );
      return response.data as DashboardCalendarResponse;
    });
    store.dispatch(end(new ServiceModel(requestId, data)));
    return data;
  } catch (err: any) {
    handleServiceError(requestId, err);
    throw err;
  }
}

export async function getTeamLeadCalendar(
  params: DashboardCalendarParams,
): Promise<DashboardCalendarResponse> {
  const requestId = REQUEST_TEAM_LEAD_DASHBOARD_CALENDAR_ID;

  try {
    store.dispatch(start(requestId));
    const data = await retryWithRenewal(async () => {
      const response = await APIKit.get(
        API_V1_TEAM_LEAD_DASHBOARD_CALENDAR,
        createDashboardRequestConfig(params),
      );
      return response.data as DashboardCalendarResponse;
    });
    store.dispatch(end(new ServiceModel(requestId, data)));
    return data;
  } catch (err: any) {
    handleServiceError(requestId, err);
    throw err;
  }
}

export async function getCallWidgetNextClient(): Promise<DashboardCallWidgetClientResponse> {
  const requestId = REQUEST_AGENT_DASHBOARD_NEXT_ID;

  try {
    store.dispatch(start(requestId));
    const data = await retryWithRenewal(async () => {
      const response = await APIKit.get(API_V1_AGENT_DASHBOARD_NEXT);
      return response.data as DashboardCallWidgetClientResponse;
    });
    store.dispatch(end(new ServiceModel(requestId, data)));
    return data;
  } catch (err: any) {
    handleServiceError(requestId, err);
    throw err;
  }
}

export async function searchCallWidgetClients(
  params: DashboardCallWidgetSearchParams,
): Promise<DashboardCallWidgetSearchResultResponse[]> {
  const requestId = REQUEST_AGENT_DASHBOARD_SEARCH_ID;

  try {
    store.dispatch(start(requestId));
    const data = await retryWithRenewal(async () => {
      const response = await APIKit.get(
        API_V1_AGENT_DASHBOARD_SEARCH,
        createDashboardRequestConfig(params),
      );
      return response.data as DashboardCallWidgetSearchResultResponse[];
    });
    store.dispatch(end(new ServiceModel(requestId, data)));
    return data;
  } catch (err: any) {
    handleServiceError(requestId, err);
    throw err;
  }
}

export async function toggleCallWidgetClientStatus(
  clientId: number,
  payload: DashboardCallWidgetToggleRequest,
): Promise<DashboardCallWidgetToggleResponse> {
  const requestId = REQUEST_AGENT_DASHBOARD_CLIENT_TOGGLE_ID;

  try {
    store.dispatch(start(requestId));
    const data = await retryWithRenewal(async () => {
      const response = await APIKit.patch(
        `${API_V1_AGENT_DASHBOARD_CLIENTS}/${clientId}/toggle`,
        payload,
      );
      return response.data as DashboardCallWidgetToggleResponse;
    });
    store.dispatch(end(new ServiceModel(requestId, data)));
    return data;
  } catch (err: any) {
    handleServiceError(requestId, err);
    throw err;
  }
}

export async function submitCallOutcome(
  clientId: number,
  payload: DashboardCallOutcomeRequest,
): Promise<DashboardCallWidgetClientResponse> {
  const requestId = REQUEST_AGENT_DASHBOARD_CALL_OUTCOME_ID;

  try {
    store.dispatch(start(requestId));
    const data = await retryWithRenewal(async () => {
      const response = await APIKit.post(
        `${API_V1_AGENT_DASHBOARD_CLIENTS}/${clientId}/call-outcome`,
        payload,
      );
      return response.data as DashboardCallWidgetClientResponse;
    });
    store.dispatch(end(new ServiceModel(requestId, data)));
    return data;
  } catch (err: any) {
    handleServiceError(requestId, err);
    throw err;
  }
}

export async function scheduleCallForAgent(payload: DashboardScheduleCallRequest): Promise<void> {
  const requestId = REQUEST_AGENT_DASHBOARD_SCHEDULED_CALLS_ID;

  try {
    store.dispatch(start(requestId));
    await retryWithRenewal(async () => {
      await APIKit.post(API_V1_AGENT_DASHBOARD_SCHEDULED_CALLS, payload);
    });
    store.dispatch(end(new ServiceModel(requestId)));
  } catch (err: any) {
    handleServiceError(requestId, err);
    throw err;
  }
}

export async function getAgentOptions(): Promise<DashboardAgentOptionResponse[]> {
  const requestId = REQUEST_AGENT_DASHBOARD_AGENT_OPTIONS_ID;

  try {
    store.dispatch(start(requestId));
    const data = await retryWithRenewal(async () => {
      const response = await APIKit.get(`${API_V1_USERS}/agents`, {
        params: { page: 0, search: "", status: "ACTIVE" },
      });
      return (response.data?.content ?? []) as DashboardAgentOptionResponse[];
    });
    store.dispatch(end(new ServiceModel(requestId, data)));
    return data;
  } catch (err: any) {
    handleServiceError(requestId, err);
    throw err;
  }
}

// Calls the existing generic To-Do delete endpoint directly (instead of TodoService.todoDelete)
// so failures reject the promise and surface an error notification, matching the rest of the
// call widget - todoDelete swallows non-401 errors instead of rethrowing them.
export async function cancelScheduledTodo(todoId: number): Promise<void> {
  const requestId = REQUEST_AGENT_DASHBOARD_CANCEL_TODO_ID + todoId;

  try {
    store.dispatch(start(requestId));
    await retryWithRenewal(async () => {
      await APIKit.delete(`${API_V1_TODO}/${todoId}`);
    });
    store.dispatch(end(new ServiceModel(requestId)));
  } catch (err: any) {
    handleServiceError(requestId, err);
    throw err;
  }
}

export async function getTeamLeadDailyKpis(
  params?: DashboardAgentFilterParams,
): Promise<DashboardTeamLeadDailyKpisResponse> {
  const requestId = REQUEST_TEAM_LEAD_DASHBOARD_DAILY_KPIS_ID;

  try {
    store.dispatch(start(requestId));
    const data = await retryWithRenewal(async () => {
      const response = await APIKit.get(
        API_V1_TEAM_LEAD_DASHBOARD_DAILY_KPIS,
        createDashboardRequestConfig(params),
      );
      return response.data as DashboardTeamLeadDailyKpisResponse;
    });
    store.dispatch(end(new ServiceModel(requestId, data)));
    return data;
  } catch (err: any) {
    handleServiceError(requestId, err);
    throw err;
  }
}
